import type { FeederSourceId } from '@kkp-fraud/shared';
import type { FieldName } from './fields';
import { FIELD_NAMES, activeCustomFields, customFieldOf, isStandardField } from './fields';
import { FEEDER_FIELDS, type FeederConfig } from './feederFields';

/**
 * The source registry — which switch sends which field, and how a raw transaction is
 * recognised as coming from it.
 *
 * Shape mirrors `registryJson()` in `docs/console-mockup/build.mjs` on purpose: this is the
 * document the API will serve once the phase-2 `source-registry-chunk2` contract lands in
 * `packages/shared`. Until then it is a typed constant here, in ONE module, so the cutover
 * is "fetch this instead of importing it" and nothing else moves.
 *
 * Two things the JSON says that a table cannot:
 *   1. Matching is `contains`, NOT equals. The domestic raw value is `ITMX_CARD`
 *      (`packages/simulator/src/codes.ts` → SWITCHING). An equals comparison would turn
 *      every domestic transaction into `UNKNOWN` with nothing to show for it.
 *   2. "does not send" and "not yet confirmed" are different facts, so they get different
 *      keys rather than both being blank.
 *
 * Three sources since KKP's field list of 23 Sep 2026 (`feederFields.ts`): ITMX arrives as
 * two feeds, Local and Single, that send different fields. How KKP's raw `switching_name`
 * tells the two apart is NOT known yet — the simulator only knows `ITMX_CARD` — so both
 * ITMX matches are the same placeholder and carry `matchConfirmed: false`.
 *
 * This `SourceId` is a registry id, not the engine's `Network` (`MASTERCARD | ITMX`).
 */

/** The same union the feeder payload contract uses (`packages/shared`). */
export type SourceId = FeederSourceId;

export interface SourceDef {
  id: SourceId;
  /** English note, kept for the registry shape (`registryJson()` in the mockup). */
  note: string;
  enabled: boolean;
  /** false — `match` is a placeholder until KKP says how this source's raw value reads. */
  matchConfirmed: boolean;
  match: {
    field: 'switching_name';
    /** `contains`, never `equals` — see the note above. */
    mode: 'contains';
    value: string;
    /** A real raw value seen from this source. */
    example: string;
  };
}

export const SOURCES: readonly SourceDef[] = [
  {
    id: 'ITMX_LOCAL',
    note: 'Domestic switch — local',
    enabled: true,
    matchConfirmed: false,
    match: { field: 'switching_name', mode: 'contains', value: 'ITMX', example: 'ITMX_CARD' },
  },
  {
    id: 'ITMX_SINGLE',
    note: 'Domestic switch — single',
    enabled: true,
    matchConfirmed: false,
    match: { field: 'switching_name', mode: 'contains', value: 'ITMX', example: 'ITMX_CARD' },
  },
  {
    id: 'MASTERCARD',
    note: 'International switch',
    enabled: true,
    matchConfirmed: true,
    match: { field: 'switching_name', mode: 'contains', value: 'MASTERCARD', example: 'MASTERCARD' },
  },
];

export const SOURCE_IDS: readonly SourceId[] = SOURCES.map((s) => s.id);

/**
 * Where each normalized field comes from on the feeder payload, per §5 of the engagement
 * spec (Data and field mapping). `de` is the ISO 8583 data element where the spec names
 * one. Mirrors the `FIELDS` table in `build.mjs` columns 4 and 5.
 */
export interface FieldOrigin {
  de: string | null;
  feeder: string;
}

export const FIELD_ORIGINS: Record<FieldName, FieldOrigin> = {
  card_id: { de: null, feeder: 'reference_card_no' },
  customer_id: { de: null, feeder: 'customer_no' },
  transaction_id: { de: null, feeder: 'transaction_id' },
  channel_id: { de: null, feeder: 'Channel.Id' },
  message_type: { de: null, feeder: 'message_type' },
  pre_auth: { de: 'DE 61.07', feeder: 'pos_tran_status' },
  event_time: { de: 'DE 7', feeder: 'transaction_datetime' },
  event_epoch_ms: { de: 'DE 7', feeder: 'transaction_datetime' },
  local_hour: { de: 'DE 12', feeder: 'local_transaction_time' },
  amount: { de: 'DE 4', feeder: 'transaction_amount' },
  amount_thb: { de: 'DE 6', feeder: 'cardholder_billing_amount' },
  currency_code_num: { de: 'DE 49', feeder: 'currency_code' },
  merchant_id: { de: 'DE 42', feeder: 'card_acceptor_id' },
  merchant_name: { de: 'DE 43', feeder: 'card_acceptor_name' },
  merchant_city: { de: 'DE 43.3', feeder: 'acceptor_city' },
  merchant_country_num: { de: 'DE 43.5', feeder: 'card_acceptor_country_code' },
  acquirer_country_num: { de: 'DE 19', feeder: 'acquiring_inst_country_code' },
  terminal_id: { de: 'DE 41', feeder: 'card_acceptor_term_id' },
  mcc: { de: 'DE 18', feeder: 'merchant_type' },
  txn_type: { de: 'DE 3', feeder: 'processing_code' },
  entry_mode: { de: 'DE 22.1', feeder: 'pos_term_pan_entry_mode' },
  pin_capable: { de: 'DE 22.2', feeder: 'pos_term_pin_entry_mode' },
  card_present: { de: 'DE 61.11', feeder: 'pos_card_indicator' },
  attended: { de: 'DE 61.01', feeder: 'pos_term_attendence' },
  is_atm: { de: 'DE 3 / DE 61.10', feeder: 'processing_code / cardholder_act_term_level' },
  ecommerce: { de: 'DE 48.42.1', feeder: 'electronic_commerce' },
  three_ds_authenticated: { de: 'DE 48.42.1', feeder: 'electronic_commerce' },
  network: { de: null, feeder: 'switching_name' },
  fraud_score: { de: 'DE 48.75.01', feeder: 'fraud_score' },
  fraud_score_reason: { de: 'DE 48.75.02', feeder: 'fraud_score_reason_code' },
  product: { de: null, feeder: 'card_product_name' },
};

/**
 * `false` — this source does not send the field at all.
 * `null` — nobody has confirmed whether it does (pending KKP).
 * string — where the field arrives on this source's feeder payload.
 */
export type Mapping = string | null | false;

/**
 * What each source shows as a normalized field's origin under the BUILT-IN feeder payload
 * (`FEEDER_FIELDS`), after the engine's own derivations in
 * `packages/rule-engine/src/normalize.ts`. This table is display text only: whether a
 * source sends the field is decided by `FIELD_DEPENDS` against the feeder payload in force
 * (see `mappingFor`), and `sources.test.ts` holds that the two agree cell for cell on the
 * default payload. The MASTERCARD column always equals `FIELD_ORIGINS`.
 */
export const SOURCE_MAPPING: Record<FieldName, Record<SourceId, Mapping>> = {
  card_id: { ITMX_LOCAL: 'reference_card_no', ITMX_SINGLE: 'reference_card_no', MASTERCARD: 'reference_card_no' },
  customer_id: { ITMX_LOCAL: 'customer_no', ITMX_SINGLE: 'customer_no', MASTERCARD: 'customer_no' },
  transaction_id: { ITMX_LOCAL: 'transaction_id', ITMX_SINGLE: 'transaction_id', MASTERCARD: 'transaction_id' },
  channel_id: { ITMX_LOCAL: 'Channel.Id', ITMX_SINGLE: 'Channel.Id', MASTERCARD: 'Channel.Id' },
  message_type: { ITMX_LOCAL: 'message_type', ITMX_SINGLE: 'message_type', MASTERCARD: 'message_type' },
  pre_auth: { ITMX_LOCAL: false, ITMX_SINGLE: false, MASTERCARD: 'DE 61.07' },
  event_time: { ITMX_LOCAL: 'DE 7', ITMX_SINGLE: 'DE 7', MASTERCARD: 'DE 7' },
  event_epoch_ms: { ITMX_LOCAL: 'DE 7', ITMX_SINGLE: 'DE 7', MASTERCARD: 'DE 7' },
  local_hour: { ITMX_LOCAL: false, ITMX_SINGLE: 'DE 12', MASTERCARD: 'DE 12' },
  amount: { ITMX_LOCAL: 'DE 4', ITMX_SINGLE: 'DE 4', MASTERCARD: 'DE 4' },
  // ITMX Single sends no DE 6; the engine falls back to DE 4.
  amount_thb: { ITMX_LOCAL: 'DE 6', ITMX_SINGLE: 'DE 4 (fallback)', MASTERCARD: 'DE 6' },
  currency_code_num: { ITMX_LOCAL: 'DE 49', ITMX_SINGLE: 'DE 49', MASTERCARD: 'DE 49' },
  merchant_id: { ITMX_LOCAL: 'DE 42', ITMX_SINGLE: 'DE 42', MASTERCARD: 'DE 42' },
  merchant_name: { ITMX_LOCAL: 'DE 43', ITMX_SINGLE: 'DE 43', MASTERCARD: 'DE 43' },
  merchant_city: { ITMX_LOCAL: false, ITMX_SINGLE: false, MASTERCARD: 'DE 43.3' },
  merchant_country_num: { ITMX_LOCAL: false, ITMX_SINGLE: false, MASTERCARD: 'DE 43.5' },
  acquirer_country_num: { ITMX_LOCAL: 'DE 19', ITMX_SINGLE: false, MASTERCARD: 'DE 19' },
  terminal_id: { ITMX_LOCAL: 'DE 41', ITMX_SINGLE: 'DE 41', MASTERCARD: 'DE 41' },
  mcc: { ITMX_LOCAL: 'DE 18', ITMX_SINGLE: 'DE 18', MASTERCARD: 'DE 18' },
  txn_type: { ITMX_LOCAL: 'DE 3', ITMX_SINGLE: 'DE 3', MASTERCARD: 'DE 3' },
  // DE 22.1 when present, else digits 1–2 of DE 22 — only Mastercard sends DE 22.1.
  entry_mode: { ITMX_LOCAL: 'DE 22 (digits 1–2)', ITMX_SINGLE: 'DE 22 (digits 1–2)', MASTERCARD: 'DE 22.1' },
  pin_capable: { ITMX_LOCAL: false, ITMX_SINGLE: false, MASTERCARD: 'DE 22.2' },
  card_present: { ITMX_LOCAL: false, ITMX_SINGLE: false, MASTERCARD: 'DE 61.11' },
  attended: { ITMX_LOCAL: false, ITMX_SINGLE: false, MASTERCARD: 'DE 61.01' },
  is_atm: { ITMX_LOCAL: 'DE 3', ITMX_SINGLE: 'DE 3', MASTERCARD: 'DE 3 / DE 61.10' },
  // ITMX Local carries the ECI in DE 60 digits 9–10; ITMX Single only has entry mode 81
  // via DE 22, which nobody has confirmed as its e-commerce signal.
  ecommerce: { ITMX_LOCAL: 'DE 60 (digits 9–10)', ITMX_SINGLE: null, MASTERCARD: 'DE 48.42.1' },
  three_ds_authenticated: { ITMX_LOCAL: 'DE 60 (digits 9–10)', ITMX_SINGLE: false, MASTERCARD: 'DE 48.42.1' },
  network: { ITMX_LOCAL: 'switching_name', ITMX_SINGLE: 'switching_name', MASTERCARD: 'switching_name' },
  fraud_score: { ITMX_LOCAL: false, ITMX_SINGLE: false, MASTERCARD: 'DE 48.75.01' },
  fraud_score_reason: { ITMX_LOCAL: false, ITMX_SINGLE: false, MASTERCARD: 'DE 48.75.02' },
  product: { ITMX_LOCAL: 'card_product_name', ITMX_SINGLE: 'card_product_name', MASTERCARD: 'card_product_name' },
};

/**
 * The raw feeder keys each normalized field is built from, in the engine's order of
 * preference (`normalize.ts`: DE 22.1 before DE 22, DE 6 before DE 4, …). A source sends
 * the normalized field when the feeder payload in force has it send AT LEAST ONE of these.
 *
 * An empty list means the value does not come from the feeder payload at all (`channel_id`
 * is the envelope's `Channel.Id`), so every source has it whatever the payload says.
 */
export const FIELD_DEPENDS: Record<FieldName, readonly string[]> = {
  card_id: ['reference_card_no'],
  customer_id: ['customer_no'],
  transaction_id: ['transaction_id'],
  channel_id: [],
  message_type: ['message_type'],
  pre_auth: ['pos_tran_status'],
  event_time: ['transaction_datetime'],
  event_epoch_ms: ['transaction_datetime'],
  local_hour: ['local_transaction_time'],
  amount: ['transaction_amount'],
  amount_thb: ['cardholder_billing_amount', 'transaction_amount'],
  currency_code_num: ['currency_code'],
  merchant_id: ['card_acceptor_id'],
  merchant_name: ['card_acceptor_name', 'acceptor_name'],
  merchant_city: ['acceptor_city'],
  merchant_country_num: ['card_acceptor_country_code'],
  acquirer_country_num: ['acquiring_inst_country_code'],
  terminal_id: ['card_acceptor_term_id'],
  mcc: ['merchant_type'],
  txn_type: ['processing_code'],
  entry_mode: ['pos_term_pan_entry_mode', 'pos_entry_mode'],
  pin_capable: ['pos_term_pin_entry_mode'],
  card_present: ['pos_card_indicator'],
  attended: ['pos_term_attendence'],
  is_atm: ['processing_code', 'cardholder_act_term_level'],
  ecommerce: ['electronic_commerce', 'additional_pos_info'],
  three_ds_authenticated: ['electronic_commerce', 'additional_pos_info'],
  network: ['switching_name'],
  fraud_score: ['fraud_score'],
  fraud_score_reason: ['fraud_score_reason_code'],
  product: ['card_product_name'],
};

/**
 * "Not confirmed" cells: a source that may carry the field by a route nobody has confirmed.
 * Applies only while the payload in force gives that source none of the field's
 * dependencies — once one is ticked, the source plainly sends it.
 */
const PENDING: Partial<Record<FieldName, readonly SourceId[]>> = {
  ecommerce: ['ITMX_SINGLE'],
};

/** Feeder rows by key, built once per config array. */
const INDEX = new WeakMap<FeederConfig, Map<string, FeederConfig[number]>>();

function indexOf(config: FeederConfig): Map<string, FeederConfig[number]> {
  let map = INDEX.get(config);
  if (!map) {
    map = new Map(config.map((row) => [row.key, row]));
    INDEX.set(config, map);
  }
  return map;
}

/** The dependencies this source sends under `config`, as `key@DE` — the cell's fingerprint. */
function sentDeps(field: FieldName, source: SourceId, config: FeederConfig): string[] {
  const rows = indexOf(config);
  const out: string[] = [];
  for (const key of FIELD_DEPENDS[field]) {
    const row = rows.get(key);
    if (row?.sends[source]) out.push(`${key}@${row.de ?? ''}`);
  }
  return out;
}

/**
 * The normalized fields that `key` alone feeds in `config`: every other feeder key they could
 * be built from is absent. Deleting `key` would leave these fields with no source at all.
 */
export function fieldsOnlyFrom(key: string, config: FeederConfig): FieldName[] {
  const keys = new Set(config.map((r) => r.key));
  return FIELD_NAMES.filter((f) => {
    const deps = FIELD_DEPENDS[f];
    return deps.includes(key) && deps.every((d) => d === key || !keys.has(d));
  });
}

export function mappingFor(field: FieldName, source: SourceId, config: FeederConfig = FEEDER_FIELDS): Mapping {
  // Wave 6 — a custom field arrives exactly as its bound feeder row does: that row's DE (or
  // key) on a source that sends it, nothing elsewhere. An unknown name is sent by no source.
  if (!isStandardField(field)) {
    const bound = customFieldOf(field);
    const row = bound ? indexOf(config).get(bound.from) : undefined;
    return row?.sends[source] ? (row.de ?? row.key) : false;
  }
  const deps = FIELD_DEPENDS[field];
  if (deps.length === 0) return FIELD_ORIGINS[field].feeder;

  const sent = sentDeps(field, source, config);
  if (sent.length === 0) return PENDING[field]?.includes(source) ? null : false;

  // Unchanged from the built-in payload for this cell: the curated text, derivations and all.
  const shown = SOURCE_MAPPING[field][source];
  if (typeof shown === 'string' && (config === FEEDER_FIELDS || sentDeps(field, source, FEEDER_FIELDS).join('|') === sent.join('|'))) {
    return shown;
  }
  // Otherwise the first dependency this source sends, by the engine's preference.
  const rows = indexOf(config);
  const first = rows.get(deps.find((k) => rows.get(k)?.sends[source])!)!;
  return first.de ?? first.key;
}

export function sendsField(source: SourceId, field: FieldName, config: FeederConfig = FEEDER_FIELDS): boolean {
  return mappingFor(field, source, config) !== false;
}

/**
 * The sources that can carry a rule reading exactly these fields — the intersection shown
 * under every condition row. A source qualifies only when it sends EVERY field the rule
 * touches; one missing field disqualifies it, because the engine would skip the rule.
 */
export function sourcesForFields(fields: readonly FieldName[], config: FeederConfig = FEEDER_FIELDS): SourceId[] {
  return SOURCE_IDS.filter((id) => fields.every((f) => sendsField(id, f, config)));
}

/**
 * The fields usable by a rule restricted to these sources — the other half of the same idea.
 * Wave 6: the standard fields, then the custom fields in scope (`useCustomFields`) whose feeder
 * row every one of those sources sends.
 */
export function fieldsForSources(sources: readonly SourceId[], config: FeederConfig = FEEDER_FIELDS): FieldName[] {
  if (sources.length === 0) return [];
  const custom = activeCustomFields()
    .map((f) => f.name as FieldName)
    .filter((f) => !isStandardField(f));
  return [...FIELD_NAMES, ...custom].filter((f) => sources.every((id) => sendsField(id, f, config)));
}
